<template>
  <div class="personal-space">
    <!-- ═══ Header ═══ -->
    <div class="space-header">
      <div class="header-left">
        <h2><i class="fas fa-th-large"></i> Mon Espace</h2>
      </div>
      <div class="tab-switcher">
        <button @click="activeTab = 'history'" :class="{ active: activeTab === 'history' }">
          <i class="fas fa-clock"></i> Historique
        </button>
        <button @click="activeTab = 'board'; loadBoard()" :class="{ active: activeTab === 'board' }">
          <i class="fas fa-columns"></i> Board
        </button>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════ -->
    <!-- ═══ HISTORY TAB (existing, untouched logic) ═══ -->
    <!-- ═══════════════════════════════════════════════════════ -->
    <div v-if="activeTab === 'history'" class="history-tab">
      <!-- Favorite Filter Chips -->
      <div v-if="favoriteGroups.length > 0 || activeFilter" class="favorite-filters">
        <button class="filter-chip" :class="{ active: !activeFilter }" @click="activeFilter = null; loadHistory()">
          <i class="fas fa-clock"></i> Tout
        </button>
        <button class="filter-chip fav-all" :class="{ active: activeFilter === 'all-favorites' }" @click="activeFilter = 'all-favorites'; loadHistory()">
          <i class="fas fa-star"></i> Favoris
        </button>
        <button v-for="group in favoriteGroups" :key="group.id" class="filter-chip"
          :class="[{ active: activeFilter === group.id }, 'chip-' + group.type]"
          @click="activeFilter = group.id; loadHistory()">
          <i :class="group.type === 'project' ? 'fas fa-folder' : group.type === 'custom' ? 'fas fa-tag' : 'fas fa-globe'"></i>
          {{ group.label }}
        </button>
        <button class="filter-chip chip-add" @click="showCreateGroup = true"><i class="fas fa-plus"></i></button>
      </div>

      <!-- Create Group -->
      <div v-if="showCreateGroup" class="create-group-bar">
        <input v-model="newGroupName" @keyup.enter="createGroup" type="text" class="group-input" placeholder="Nom du groupe..." />
        <button @click="createGroup" class="group-save-btn" :disabled="!newGroupName.trim()"><i class="fas fa-check"></i></button>
        <button @click="showCreateGroup = false; newGroupName = ''" class="group-cancel-btn"><i class="fas fa-times"></i></button>
      </div>

      <!-- Search -->
      <div class="history-search">
        <i class="fas fa-search search-icon"></i>
        <input v-model="searchTerm" @input="onSearchInput" type="text" class="search-input" placeholder="Rechercher par nom, projet, ou requête..." />
        <i v-if="searchTerm" @click="searchTerm = ''; loadHistory()" class="fas fa-times search-clear"></i>
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="loading-state"><div class="loading-spinner"></div><p>Chargement...</p></div>

      <!-- Empty -->
      <div v-else-if="days.length === 0" class="empty-state">
        <i class="fas fa-eye-slash"></i>
        <h3>{{ searchTerm || activeFilter ? 'Aucun résultat' : 'Aucun historique' }}</h3>
        <p>{{ searchTerm ? 'Essayez un autre terme.' : 'Les profils consultés apparaîtront ici.' }}</p>
      </div>

      <!-- Day Groups -->
      <div v-else class="history-days">
        <div v-for="day in days" :key="day.date" class="day-group">
          <div class="day-header">
            <span class="day-label">{{ formatDayLabel(day.date) }}</span>
            <span class="day-count">{{ day.views.length }} profil{{ day.views.length > 1 ? 's' : '' }}</span>
          </div>
          <div class="day-entries">
            <div v-for="entry in day.views" :key="entry.elasticsearch_id" class="history-entry">
              <div class="entry-left" @click="goToCandidate(entry.elasticsearch_id)">
                <div class="entry-time">{{ formatTime(entry.viewed_at) }}</div>
                <div class="entry-details">
                  <span class="entry-name">{{ entry.name }}</span>
                  <span v-if="entry.title" class="entry-title">{{ entry.title }}</span>
                </div>
              </div>
              <div class="entry-right">
                <span v-if="entry.favorite_project_id" class="fav-badge" :class="getFavClass(entry.favorite_project_id)">
                  <i class="fas fa-star"></i> {{ getFavLabel(entry) }}
                </span>
                <span v-if="entry.project_name" class="entry-project"><i class="fas fa-folder"></i> {{ entry.project_name }}</span>
                <span v-if="entry.search_query" class="entry-query"
                  :class="{ expanded: expandedQueries[entry.elasticsearch_id] }"
                  @mouseenter="expandedQueries[entry.elasticsearch_id] = true"
                  @mouseleave="expandedQueries[entry.elasticsearch_id] = false"
                  :title="entry.search_query">
                  <i class="fas fa-search"></i> {{ entry.search_query }}
                </span>
                <div class="fav-wrapper">
                  <button class="fav-btn" :class="{ active: entry.favorite_project_id }" @click.stop="toggleFavDropdown(entry.elasticsearch_id)">
                    <i :class="entry.favorite_project_id ? 'fas fa-star' : 'far fa-star'"></i>
                  </button>
                  <div v-if="favDropdownOpen === entry.elasticsearch_id" class="fav-dropdown" @click.stop>
                    <button class="fav-option" @click="setFavorite(entry, 'global')"><i class="fas fa-globe"></i> Global</button>
                    <button v-for="proj in agentProjects" :key="proj.project_id" class="fav-option" @click="setFavorite(entry, proj.project_id)">
                      <i class="fas fa-folder"></i> {{ proj.project_name }}
                    </button>
                    <div v-if="customGroups.length" class="fav-divider"></div>
                    <button v-for="g in customGroups" :key="g.id" class="fav-option" @click="setFavorite(entry, g.id)">
                      <i class="fas fa-tag"></i> {{ g.label }}
                    </button>
                    <div class="fav-divider"></div>
                    <button v-if="entry.favorite_project_id" class="fav-option fav-remove" @click="removeFavorite(entry)"><i class="fas fa-times"></i> Retirer</button>
                  </div>
                </div>
                <button class="archive-btn" @click.stop="archiveEntry(entry)" title="Masquer"><i class="fas fa-eye-slash"></i></button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="total > pageSize" class="pagination">
        <button @click="prevPage" :disabled="currentPage === 1" class="page-btn"><i class="fas fa-chevron-left"></i></button>
        <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
        <button @click="nextPage" :disabled="currentPage >= totalPages" class="page-btn"><i class="fas fa-chevron-right"></i></button>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════ -->
    <!-- ═══ BOARD TAB (Trello-style Kanban) ═══ -->
    <!-- ═══════════════════════════════════════════════════════ -->
    <div v-if="activeTab === 'board'" class="board-tab">
      <div v-if="boardLoading" class="loading-state"><div class="loading-spinner"></div><p>Chargement du board...</p></div>

  <!-- Board Search -->
  <div v-show="!boardLoading && boardColumns.length" class="board-search-bar">
    <i class="fas fa-search board-search-icon"></i>
    <input
      v-model="boardSearch"
      type="text"
      class="board-search-input"
      placeholder="Rechercher par nom, poste, label, sticker..."
    />
    <i v-if="boardSearch" @click="boardSearch = ''" class="fas fa-times board-search-clear"></i>
  </div>
  <div v-show="!boardLoading && boardColumns.length" class="search-help-tip">
    <i class="fas fa-info-circle"></i>
    <span><b>python java</b> = les deux · <b>python or java</b> = l'un ou l'autre · <b>Global: senior</b> = chercher dans un board</span>
  </div>

      <div v-if="boardColumns.length === 0" class="empty-state">
        <i class="fas fa-columns"></i>
        <h3>Aucune colonne</h3>
        <p>Ajoutez des candidats aux favoris depuis l'historique pour les voir ici.</p>
      </div>

      <div v-else class="board-canvas" ref="boardCanvas">
        <!-- Columns -->
        <div v-for="(col, colIdx) in filteredBoardColumns" :key="col.id" class="board-column"
        :class="{ 'col-dragging': dragColIndex === colIdx }"
        :style="getColTint(col)"
        @dragover.prevent="onDragOver($event, col)"
        @dragleave="onDragLeave($event)"
        @drop="onDrop($event, col)">
          <!-- Column Header -->
          
          <div class="col-header"
          draggable="true"
          @dragstart.stop="onColDragStart($event, colIdx)"
          @dragend="onColDragEnd">
                      <div class="col-header-left">
              <span class="col-dot" :style="{ background: col.color }"></span>
              <span class="col-title">{{ col.label }}</span>
              <span v-if="col.scope === 'project'" class="col-shared-badge"><i class="fas fa-users"></i></span>
              <span class="col-count">{{ col.cards.length }}</span>
                            <span class="col-count">{{ col.cards.length }}</span>
              <div class="col-color-wrapper">
                <span class="col-color-trigger" @click.stop="toggleColColor(col.id)"><i class="fas fa-palette"></i></span>
                <div v-if="colColorOpen === col.id" class="col-color-picker" @click.stop>
                  <span v-for="c in columnColors" :key="c" class="col-color-dot"
                    :style="{ background: c }" :class="{ active: col.color === c }"
                    @click="setColColor(col, c)"></span>
                </div>
              </div>              
            </div>
            <div class="col-header-right">
              <button class="col-action-btn" @click.stop="duplicateBoard(col)" title="Dupliquer ce board">
                <i class="far fa-clone"></i>
              </button>
              <button v-if="col.note_count || boardNoteColumn === col.id" class="col-note-badge" @click.stop="toggleBoardNotes(col)">
                <i class="far fa-sticky-note"></i> {{ col.note_count || '' }}
              </button>
              <button class="col-note-btn" @click.stop="toggleBoardNotes(col)" title="Notes du board">
                <i class="far fa-sticky-note"></i>
              </button>
            </div>
          </div>

          <!-- Board Notes Panel (inline under header) -->
          <div v-if="boardNoteColumn === col.id" class="board-notes-panel" @click.stop>
            <div class="board-note-input-row">
              <input v-model="newBoardNote" @keyup.enter="addBoardNote(col.id)" type="text" class="board-note-input" placeholder="Note sur ce board..." />
              <button @click="addBoardNote(col.id)" :disabled="!newBoardNote.trim()" class="board-note-send"><i class="fas fa-plus"></i></button>
            </div>
            <div v-for="n in boardNotes" :key="n.id" class="board-note-item">
              <span class="board-note-text">{{ n.text }}</span>
              <div class="board-note-meta">
                <span>{{ formatCommentDate(n.created_at) }}</span>
                <span>{{ n.agent_name }}</span>
                <button @click="deleteBoardNote(n.id, col.id)" class="board-note-del"><i class="fas fa-trash"></i></button>
              </div>
            </div>
          </div>
          <!-- Cards -->
          <div class="col-cards">
             <!-- In-board search -->
             <div class="col-search-row">
              <input
                v-model="colSearch[col.id]"
                @input="onColSearch(col)"
                type="text"
                class="col-search-input"
                :placeholder="`Chercher dans ${col.label}...`"
              />
              <i v-if="colSearchLoading[col.id]" class="fas fa-spinner fa-spin col-search-spin"></i>

              <i v-if="colSearch[col.id]" @click="colSearch[col.id] = ''; colSearchResults[col.id] = null" class="fas fa-times col-search-clear"></i>
            </div>
            <div v-for="card in getColCards(col)" :key="card.elasticsearch_id"
              class="board-card"
              :class="{ dragging: dragCard?.elasticsearch_id === card.elasticsearch_id }"
              :style="getCardTint(card)"
              draggable="true"
              @dragstart="onDragStart($event, card, col)"
              @dragend="onDragEnd"
              @click="openCardModal(card, col)"
            >
              <!-- Label bars at top -->
              <div v-if="card.labels?.length" class="card-labels">
                <span v-for="lbl in card.labels" :key="lbl.label" class="card-label-bar" :style="{ background: lbl.color }" :title="lbl.label"></span>
              </div>

              <!-- Sticker -->
              <span v-if="card.sticker" class="card-sticker">{{ card.sticker }}</span>

              <!-- Card content -->
              <div class="card-name">{{ card.name }}</div>
              <div v-if="card.title" class="card-title">{{ card.title }}</div>
              <!-- Pipeline status -->
              <div v-if="card.pipeline?.length" class="card-pipeline">
                <span v-for="p in card.pipeline.slice(0, 2)" :key="p.project_id"
                  class="card-pipe-pill" :class="'pipe-' + getPipelineGroup(p.status)">
                  {{ formatWorkflowStatus(p.status) }}
                </span>
                <span v-if="card.pipeline.length > 2" class="card-pipe-more">+{{ card.pipeline.length - 2 }}</span>
              </div>
              <!-- Card footer -->
              <div class="card-footer">
                <span v-if="card.location" class="card-meta"><i class="fas fa-map-marker-alt"></i> {{ card.location }}</span>
                <span v-if="card.added_at" class="card-meta"><i class="fas fa-plus-circle"></i> {{ formatShortDate(card.added_at) }}</span>
                <div class="card-icons">
                  <span v-if="card.comment_count" class="card-icon-badge"><i class="far fa-comment"></i> {{ card.comment_count }}</span>
                  <span v-if="card.labels?.length" class="card-icon-badge"><i class="fas fa-tag"></i> {{ card.labels.length }}</span>
                </div>
              </div>
            </div>


          </div>

          <!-- Add Card Placeholder -->
         <!-- Add Card -->
         <div class="col-add-card">
          <div v-if="addCardCol !== col.id" class="col-add-btn-inline" @click="addCardCol = col.id; addCardQuery = ''; addCardResults = []">
            <i class="fas fa-plus"></i> Ajouter un candidat
          </div>
          <div v-else class="add-card-search" @click.stop>
            <input
              v-model="addCardQuery"
              @input="onAddCardSearch"
              type="text"
              class="add-card-input"
              placeholder="Nom, email, téléphone..."
              ref="addCardInput"
            />
            <button @click="addCardCol = null" class="add-card-close"><i class="fas fa-times"></i></button>
            <div v-if="addCardLoading" class="add-card-status"><i class="fas fa-spinner fa-spin"></i></div>
            <div v-if="addCardResults.length" class="add-card-results">
              <div v-for="r in addCardResults" :key="r.candidate_id" class="add-card-result"
                @click="addCandidateToBoard(r, col)">
                <span class="acr-name">{{ r.name }}</span>
                <span class="acr-title">{{ r.title }}</span>
              </div>
            </div>
            <div v-if="addCardQuery.length >= 3 && !addCardLoading && !addCardResults.length" class="add-card-empty">
              Aucun résultat
            </div>
          </div>
        </div>
        </div>

        <!-- Add Column -->
        <div class="add-column" @click="showCreateGroup = true; activeTab = 'board'">
          <div v-if="!boardAddingColumn" class="add-col-btn" @click="boardAddingColumn = true">
            <i class="fas fa-plus"></i> Ajouter une liste
          </div>
          <div v-else class="add-col-form" @click.stop>
            <input v-model="newGroupName" @keyup.enter="createGroupAndRefreshBoard" type="text" class="add-col-input" placeholder="Nom de la liste..." ref="addColInput" />
            <div class="add-col-actions">
              <button @click="createGroupAndRefreshBoard" class="add-col-save" :disabled="!newGroupName.trim()">Ajouter</button>
              <button @click="boardAddingColumn = false; newGroupName = ''" class="add-col-cancel"><i class="fas fa-times"></i></button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════ -->
    <!-- ═══ CARD DETAIL MODAL ═══ -->
    <!-- ═══════════════════════════════════════════════════════ -->
    <teleport to="body">
      <div v-if="modalCard" class="card-modal-overlay" @click.self="closeModal">
        <div class="card-modal">
          <!-- Modal Header -->
          <div class="modal-header">
            <div class="modal-header-info">
              <h3>{{ modalCard.name }}</h3>
              <span v-if="modalCard.title" class="modal-subtitle">{{ modalCard.title }}</span>
              <span class="modal-col-badge" :style="{ borderColor: modalColumn?.color, color: modalColumn?.color }">
                <i class="fas fa-columns"></i> {{ modalColumn?.label }}
              </span>
              <div v-if="modalCard.boards?.length > 1" class="modal-also-in">
                <span class="also-label">Aussi dans:</span>
                <span v-for="bid in modalCard.boards.filter(b => b !== modalColumn?.id)" :key="bid"
                  class="also-badge" :class="bid === 'global' ? 'also-global' : bid.startsWith('custom:') ? 'also-custom' : 'also-project'">
                  {{ getBoardLabel(bid) }}
                </span>
              </div>
           
           
           
           <!-- Pipeline status banner -->
           <div v-if="modalProjectHistory?.length" class="modal-pipeline-banner">
            <div v-for="proj in modalProjectHistory.filter(p => p.workflow_status)" :key="proj.project_id" class="mpb-item" :class="'mpb-' + getPipelineGroup(proj.workflow_status)">
              <span class="mpb-status">{{ formatWorkflowStatus(proj.workflow_status) }}</span>
              <span class="mpb-project">{{ proj.project_name }}</span>
            </div>
          </div>
            </div>
            <button @click="closeModal" class="modal-close"><i class="fas fa-times"></i></button>
          </div>

          <div class="modal-body">
            <!-- Left: Content -->
            <div class="modal-content">
              <!-- Labels -->
              <div class="modal-section">
                <h4><i class="fas fa-tag"></i> Étiquettes</h4>
                <div class="modal-labels">
                  <span v-for="lbl in modalCard.labels" :key="lbl.label" class="modal-label" :style="{ background: lbl.color }">
                    {{ lbl.label }}
                    <i class="fas fa-times label-remove" @click="removeLabel(lbl.label)"></i>
                  </span>
                  <button class="add-label-btn" @click="showLabelPicker = !showLabelPicker"><i class="fas fa-plus"></i></button>
                </div>
                <!-- Label Picker -->
                <div v-if="showLabelPicker" class="label-picker">
                  <div v-for="preset in labelPresets" :key="preset.label" class="label-preset"
                    :class="{ used: modalCard.labels?.some(l => l.label === preset.label) }"
                    @click="addLabel(preset)">
                    <span class="label-dot" :style="{ background: preset.color }"></span>
                    {{ preset.label }}
                  </div>
                  <div class="label-custom-row">
                    <input v-model="customLabelText" type="text" placeholder="Perso..." class="label-custom-input" @keyup.enter="addCustomLabel" />
                    <div class="label-color-dots">
                      <span v-for="c in labelColors" :key="c" class="color-dot" :style="{ background: c }" :class="{ active: customLabelColor === c }" @click="customLabelColor = c"></span>
                    </div>
                    <button @click="addCustomLabel" class="label-add-btn" :disabled="!customLabelText.trim()"><i class="fas fa-plus"></i></button>
                  </div>
                </div>
              </div>

              <!-- Sticker -->
              <div class="modal-section">
                <h4><i class="fas fa-smile"></i> Sticker</h4>
                <div class="sticker-picker">
                  <span v-for="s in stickerOptions" :key="s" class="sticker-option"
                    :class="{ active: modalCard.sticker === s }"
                    @click="setSticker(modalCard.sticker === s ? null : s)">{{ s }}</span>
                  <span v-if="modalCard.sticker" class="sticker-clear" @click="setSticker(null)"><i class="fas fa-times"></i></span>
                </div>
              </div>

              <!-- Comments -->
              <div class="modal-section">
                <h4><i class="far fa-comment"></i> Notes <span v-if="modalComments.length">({{ modalComments.length }})</span></h4>
                <div class="comment-input-row">
                  <textarea v-model="newComment" class="comment-input" placeholder="Écrire une note..." rows="2" @keydown.ctrl.enter="addComment" @keydown.meta.enter="addComment"></textarea>
                  <button @click="addComment" class="comment-submit" :disabled="!newComment.trim()">
                    <i class="fas fa-paper-plane"></i>
                  </button>
                </div>
                <div class="comments-list">
                  <div v-for="c in modalComments" :key="c.id" class="comment-item">
                    <div v-if="c.agent_name && modalColumn?.scope === 'project'" class="comment-author">{{ c.agent_name }}</div>
                    <div class="comment-text">{{ c.text }}</div>
                    <div class="comment-meta">
                      <span>{{ formatCommentDate(c.created_at) }}</span>
                      <button v-if="modalColumn?.scope !== 'project' || c.agent_id === currentUserId || isAdmin"
                        @click="deleteComment(c.id)" class="comment-delete"><i class="fas fa-trash"></i></button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Project History -->
              <div class="modal-section">
                <h4><i class="fas fa-briefcase"></i> Projets <span v-if="modalProjectHistory?.length">({{ modalProjectHistory.length }})</span></h4>
                <div v-if="modalProjectHistoryLoading" class="ph-loading">
                  <i class="fas fa-spinner fa-spin"></i> Chargement...
                </div>
                <div v-else-if="!modalProjectHistory?.length" class="ph-empty">
                  Aucun projet
                </div>
                <!-- <div v-else class="ph-list">
                  <div v-for="proj in modalProjectHistory" :key="proj.project_id" class="ph-item">
                    <div class="ph-top">
                      <div class="ph-info">
                        <span class="ph-name">{{ proj.project_name }}</span>
                        <span class="ph-meta">{{ proj.job_title }} · {{ proj.client_name }}</span>
                      </div>
                      <span class="ph-status" :class="'ph-' + (proj.project_status || '').toLowerCase()">
                        {{ proj.project_status || '—' }}
                      </span>
                    </div>
                    <div v-if="proj.workflow_status" class="ph-workflow">
                      <span class="ph-workflow-badge" :class="'phw-' + proj.workflow_status">
                        {{ formatWorkflowStatus(proj.workflow_status) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div> -->
              

              <div v-else class="ph-list">
                <div v-for="proj in modalProjectHistory" :key="proj.project_id" class="ph-item">
                  <div class="ph-top">
                    <div class="ph-info">
                      <span class="ph-name">{{ proj.project_name }}</span>
                      <span class="ph-meta">{{ proj.job_title }} · {{ proj.client_name }}</span>
                    </div>
                    <span class="ph-status" :class="'ph-' + (proj.project_status || '').toLowerCase()">
                      {{ proj.project_status || '—' }}
                    </span>
                  </div>
                  <div v-if="proj.workflow_status" class="ph-workflow">
                    <span class="ph-workflow-badge" :class="'phw-' + proj.workflow_status">
                      {{ formatWorkflowStatus(proj.workflow_status) }}
                    </span>
                  </div>
                  <!-- Report actions -->
                  <div class="ph-actions">
                    <button v-if="proj.has_been_screened"
                      class="ph-action-btn ph-view-report"
                      @click.stop="viewReportInModal(proj)"
                      :class="{ active: modalReportProject?.project_id === proj.project_id }">
                      <i class="fas fa-file-alt"></i> Voir rapport
                    </button>
                    <button v-if="proj.workflow_status === 'screening-in-progress'"
                      class="ph-action-btn ph-write-report"
                      @click.stop="goToWriteReport(proj)">
                      <i class="fas fa-pen"></i> Rédiger rapport
                    </button>
                  </div>
                  <!-- Inline report viewer -->
                  <div v-if="modalReportProject?.project_id === proj.project_id" class="ph-report-inline">
                    <div v-if="modalReportLoading" class="ph-report-loading"><i class="fas fa-spinner fa-spin"></i> Chargement...</div>
                    <div v-else-if="modalReport" class="ph-report-content">
                      <div class="ph-report-meta">
                        <span :class="['ph-report-status', modalReport.status]">{{ modalReport.status }}</span>
                        <span v-if="modalReport.workflow_status" class="ph-report-workflow">
                          {{ modalReport.workflow_status === 'tech_completed' ? 'HR + Tech' : modalReport.workflow_status === 'hr_submitted' ? 'RH seule' : modalReport.workflow_status }}
                        </span>
                        <span v-if="modalReport.completed_at" class="ph-report-date">{{ formatCommentDate(modalReport.completed_at) }}</span>
                      </div>
                      <!-- HR Section -->
                      <div v-if="modalReport.report_data?.hr_section" class="ph-report-section">
                        <h5><i class="fas fa-user-tie"></i> Éval. RH</h5>
                        <div v-for="(val, key) in modalReport.report_data.hr_section" :key="key" class="ph-report-field">
                          <span class="ph-field-label">{{ formatFieldLabel(key) }}</span>
                          <span class="ph-field-value">{{ typeof val === 'object' ? JSON.stringify(val) : (val || '—') }}</span>
                        </div>
                      </div>
                      <!-- Tech Section -->
                      <div v-if="modalReport.report_data?.tech_section" class="ph-report-section">
                        <h5><i class="fas fa-laptop-code"></i> Éval. Technique</h5>
                        <div v-for="(val, key) in modalReport.report_data.tech_section" :key="key" class="ph-report-field">
                          <span class="ph-field-label">{{ formatFieldLabel(key) }}</span>
                          <span class="ph-field-value">{{ typeof val === 'object' ? JSON.stringify(val) : (val || '—') }}</span>
                        </div>
                      </div>
                      <!-- Legacy -->
                      <div v-if="!modalReport.report_data?.hr_section && !modalReport.report_data?.tech_section" class="ph-report-section">
                        <div v-if="modalReport.report_data?.summary" class="ph-report-field">
                          <span class="ph-field-label">Résumé</span>
                          <span class="ph-field-value">{{ modalReport.report_data.summary }}</span>
                        </div>
                        <div v-if="modalReport.report_data?.recommendation" class="ph-report-field">
                          <span class="ph-field-label">Recommandation</span>
                          <span class="ph-field-value">{{ modalReport.report_data.recommendation }}</span>
                        </div>
                      </div>
                    </div>
                    <div v-else class="ph-report-empty">Aucun rapport trouvé</div>
                  </div>
                </div>
              </div>
            </div>
              <!-- Info -->
              <div class="modal-section modal-info">
                <div v-if="modalCard.search_query" class="info-row"><i class="fas fa-search"></i> {{ modalCard.search_query }}</div>
                <div v-if="modalCard.viewed_at" class="info-row"><i class="fas fa-eye"></i> Vu le {{ formatCommentDate(modalCard.viewed_at) }}</div>
              </div>
            </div>

            <!-- Right: Actions -->
            <div class="modal-actions">
              <h5>Actions</h5>
              <button @click="goToCandidate(modalCard.elasticsearch_id); closeModal()" class="action-btn"><i class="fas fa-external-link-alt"></i> Voir Profil</button>
              <button @click="showMovePicker = !showMovePicker" class="action-btn"><i class="fas fa-plus"></i> Ajouter à...</button>
              <div class="action-divider"></div>
              <h5>Pipeline</h5>
              <button v-if="!hasActiveScreening" @click="showProjectLinkPicker = !showProjectLinkPicker" class="action-btn action-pipeline">
                <i class="fas fa-paper-plane"></i> Proposer screening
              </button>
              <div v-if="hasActiveScreening" class="action-blocked">
                <i class="fas fa-lock"></i> Screening en cours
              </div>
              <div v-if="showProjectLinkPicker" class="move-picker">
                <button v-for="proj in availableProjectsForLink" :key="proj.project_id" class="move-option"
                  @click="startScreeningFromBoard(proj)">
                  <i class="fas fa-briefcase"></i> {{ proj.project_name }}
                </button>
                <div v-if="!availableProjectsForLink.length" class="move-option" style="opacity:0.3;cursor:default;">
                  Aucun projet disponible
                </div>
              </div>
              <div class="action-divider"></div>
              <div v-if="showMovePicker" class="move-picker">
                <button v-for="col in boardColumns" :key="col.id" class="move-option"
                  :class="{ current: col.id === modalColumn?.id }"
                  @click="moveCard(col.id)">
                  <span class="col-dot-sm" :style="{ background: col.color }"></span> {{ col.label }}
                </button>
              </div>
              <button v-if="modalColumn?.scope !== 'project' || isAdmin"
              @click="removeFromBoard" class="action-btn action-danger"><i class="fas fa-times"></i> Retirer du Board</button>            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'HistorySection',
  data() {
    return {
      currentUserId: '',
      isAdmin: false,
      // Report viewer
      modalReport: null,
      modalReportLoading: false,
      modalReportProject: null,
      //modal pipeline 
      
      showProjectLinkPicker: false,
      linkingToProject: false,

       modalProjectHistory: null,  // null = not loaded, [] = loaded empty
      modalProjectHistoryLoading: false,
      duplicatingBoard: null,
      colColorOpen: null,
      columnColors: ['#22c55e','#3b82f6','#f59e0b','#a855f7','#ef4444','#06b6d4','#ec4899','#f97316','#64748b','#14b8a6'],
      dragColIndex: null,
      colSearch: {},
      colSearchLoading: {},
      colSearchResults: {},  // col.id → Set of matching es_ids from ES
      colSearchDebounce: {},
      addCardCol: null,
      addCardQuery: '',
      addCardResults: [],
      addCardLoading: false,
      addCardDebounce: null,
      // Board notes
      boardNoteColumn: null,
      boardNotes: [],
      newBoardNote: '',
      
      boardSearch: '',
      activeTab: 'history',
      // ─── History ───
      days: [], total: 0, isLoading: false, currentPage: 1, pageSize: 50,
      searchTerm: '', searchDebounce: null, expandedQueries: {},
      favoriteGroups: [], activeFilter: null, agentProjects: [],
      favDropdownOpen: null, showCreateGroup: false, newGroupName: '',
      // ─── Board ───
      boardColumns: [], boardLoading: false, boardAddingColumn: false,
      dragCard: null, dragSourceCol: null,
      // ─── Card Modal ───
      modalCard: null, modalColumn: null, modalComments: [],
      newComment: '', showLabelPicker: false, showMovePicker: false,
      customLabelText: '', customLabelColor: '#93c5fd',
      // ─── Presets ───
      labelPresets: [
        { label: 'Junior', color: '#06b6d4' },
        { label: 'Confirmé', color: '#3b82f6' },
        { label: 'Senior', color: '#a855f7' },
        { label: 'Expert', color: '#ec4899' },
        { label: 'Disponible', color: '#22c55e' },
        { label: 'Préavis 1m', color: '#f59e0b' },
        { label: 'Préavis 3m', color: '#f97316' },
        { label: 'Pas dispo', color: '#ef4444' },
        { label: 'TJM300', color: '#14b8a6' },
        { label: 'TJM400', color: '#8b5cf6' },
        { label: 'TJM12000', color: '#6366f1' },
        { label: 'Freelance', color: '#0ea5e9' },
        { label: 'CDI only', color: '#64748b' },
      ],
      labelColors: ['#ef4444','#f59e0b','#22c55e','#3b82f6','#a855f7','#06b6d4','#ec4899','#8b5cf6','#14b8a6','#f97316'],
      stickerOptions: ['🟢','🟡','🔴','⭐','🔥','✅','⏳','💎','🎯','❌','📞','🤝'],
    };
  },
  computed: {
    hasActiveScreening() {
      if (!this.modalProjectHistory?.length) return false;
      const blocking = ['screening-in-progress', 'tech-interview-requested', 'tech-interview-confirmed', 'tech-interview-scheduled'];
      return this.modalProjectHistory.some(p => blocking.includes(p.workflow_status));
    },
    availableProjectsForLink() {
      if (!this.modalProjectHistory) return this.agentProjects;
      const existingProjectIds = new Set(this.modalProjectHistory.map(p => p.project_id));
      return this.agentProjects.filter(p => !existingProjectIds.has(p.project_id));
    },
    totalPages() { return Math.ceil(this.total / this.pageSize); },
    customGroups() { return this.favoriteGroups.filter(g => g.type === 'custom'); },
    // filteredBoardColumns() {
    //   if (!this.boardSearch.trim()) return this.boardColumns;
    //   const raw = this.boardSearch.trim().toLowerCase();

    //   // Split on " or " (case insensitive), then each OR-group is AND'd
    //   const orGroups = raw.split(/\s+or\s+/i).map(g => g.trim().split(/\s+/));

    //   return this.boardColumns.map(col => {
    //     // Column-level match (title matches = show all cards)
    //     const colHaystack = col.label.toLowerCase();
    //     const colMatches = orGroups.some(terms => terms.every(t => colHaystack.includes(t)));
    //     if (colMatches) return col;

    //     // Card-level match
    //     return {
    //       ...col,
    //       cards: col.cards.filter(card => {
    //         const haystack = [
    //           card.name, card.title, card.location, card.sticker, card.search_query,
    //           ...(card.labels || []).map(l => l.label),
    //         ].filter(Boolean).join(' ').toLowerCase();
    //         return orGroups.some(terms => terms.every(t => haystack.includes(t)));
    //       })
    //     };
    //   }).filter(col => col.cards.length > 0);
    // },

    filteredBoardColumns() {
      if (!this.boardSearch.trim()) return this.boardColumns;
      const raw = this.boardSearch.trim();

      // Check for board_name: prefix operator
      const scopeMatch = raw.match(/^(\w[\w\s]*?):\s*(.+)$/);
      let scopeBoard = null;
      let searchText = raw;
      if (scopeMatch) {
        scopeBoard = scopeMatch[1].trim().toLowerCase();
        searchText = scopeMatch[2].trim();
      }

      const lower = searchText.toLowerCase();
      const orGroups = lower.split(/\s+or\s+/i).map(g => g.trim().split(/\s+/));

      const matchesTerms = (haystack) => orGroups.some(terms => terms.every(t => haystack.includes(t)));

      return this.boardColumns
        .filter(col => !scopeBoard || col.label.toLowerCase().includes(scopeBoard))
        .map(col => {
          const colHaystack = [col.label, col.note_text || ''].join(' ').toLowerCase();
          if (matchesTerms(colHaystack)) return col;

          return {
            ...col,
            cards: col.cards.filter(card => {
              const haystack = [
                card.name, card.title, card.location, card.sticker, card.search_query,
                card.comment_text || '',
                ...(card.labels || []).map(l => l.label),
              ].filter(Boolean).join(' ').toLowerCase();
              return matchesTerms(haystack);
            })
          };
        }).filter(col => col.cards.length > 0);
    },
  },
  async mounted() {
    this.loadHistory();
    this.loadFavoriteGroups();
    this.loadAgentProjects();
    try {
      const res = await axios.get('/api/v1/clients/user/type');
      this.currentUserId = res.data.user_id || '';
      this.isAdmin = res.data.user_type === 'admin';
    } catch (e) { /* */ }
    document.addEventListener('click', this.closeFavDropdown);
  },
  beforeUnmount() { document.removeEventListener('click', this.closeFavDropdown); },
  methods: {
    getColTint(col) {
      if (!col.color) return {};
      const hex = col.color;
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return {
        '--col-tint': `rgba(${r},${g},${b},0.04)`,
        '--col-tint-border': `rgba(${r},${g},${b},0.1)`,
      };
    },
    getCardTint(card) {
      if (!card.labels?.length) return {};
      // Use first label's color as the card tint
      const hex = card.labels[0].color;
      if (!hex) return {};
      // Parse hex to RGB
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return {
        '--card-tint': `rgba(${r},${g},${b},0.06)`,
        '--card-tint-hover': `rgba(${r},${g},${b},0.1)`,
        '--card-tint-border': `rgba(${r},${g},${b},0.12)`,
      };
    },
  //pipeline 
  async viewReportInModal(proj) {
      // Toggle off if same project
      if (this.modalReportProject?.project_id === proj.project_id) {
        this.modalReportProject = null;
        this.modalReport = null;
        return;
      }
      this.modalReportProject = proj;
      this.modalReport = null;
      this.modalReportLoading = true;
      try {
        const res = await axios.get(`/api/v1/screening-reports/candidate/${this.modalCard.elasticsearch_id}`);
        const reports = Array.isArray(res.data) ? res.data : (res.data.reports || []);
        const match = reports.find(r => r.project_id === proj.project_id && r.status === 'completed')
          || reports.find(r => r.project_id === proj.project_id);
        this.modalReport = match || null;
      } catch (e) { this.modalReport = null; }
      finally { this.modalReportLoading = false; }
    },

    goToWriteReport(proj) {
      this.closeModal();
      this.$router.push({
        path: '/',
        query: {
          candidate_id: this.modalCard.elasticsearch_id,
          project_id: proj.project_id,
          auto_search: 'true'
        }
      });
    },

    formatFieldLabel(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },
  async startScreeningFromBoard(project) {
      if (!this.modalCard) return;
      const esId = this.modalCard.elasticsearch_id;
      this.linkingToProject = true;
      try {
        // 1. Add to project pool
        await axios.post(`/api/v1/projects/${project.project_id}/candidate_pool/add/`, {
          candidate_elasticsearch_id: esId,
          interaction_type: 'consulted'
        });
        // 2. Set screening status
        await axios.post(`/api/v1/candidates/${esId}/tags`, {
          tag_type: 'project_status',
          tag_value: 'screening-in-progress',
          project_id: project.project_id
        });
        // 3. Update local modal state
        if (!this.modalProjectHistory) this.modalProjectHistory = [];
        this.modalProjectHistory.push({
          project_id: project.project_id,
          project_name: project.project_name,
          job_title: project.job_title || '',
          client_name: '',
          workflow_status: 'screening-in-progress',
          project_status: 'ACTIVE'
        });
        // 4. Update card pipeline in board
        const card = this.findBoardCard(esId);
        if (card) {
          if (!card.pipeline) card.pipeline = [];
          card.pipeline.push({ project_id: project.project_id, status: 'screening-in-progress' });
        }
        this.showProjectLinkPicker = false;
        alert(`Screening démarré pour "${project.project_name}"`);
      } catch (e) {
        console.error('Start screening failed:', e);
        alert('Échec du lancement du screening');
      } finally {
        this.linkingToProject = false;
      }
    },
  getPipelineGroup(status) {
      if (!status) return 'none';
      if (['hired'].includes(status)) return 'done';
      if (['rejected', 'candidate-declined'].includes(status)) return 'out';
      if (['screened', 'interview-done', 'tech-interview-done'].includes(status)) return 'complete';
      if (status.includes('interview') || status.includes('screening-in-progress')) return 'active';
      if (status === 'request-screening') return 'pending';
      return 'none';
    },
    formatWorkflowStatus(status) {
      if (!status) return '';
      const map = {
        'request-screening': 'Screening demandé',
        'screening-in-progress': 'Screening en cours',
        'screened': 'Screené',
        'interview-requested': 'Entretien demandé',
        'interview-scheduled': 'Entretien planifié',
        'interview-confirmed': 'Entretien confirmé',
        'interview-done': 'Entretien fait',
        'tech-interview-requested': 'Tech demandé',
        'tech-interview-confirmed': 'Tech confirmé',
        'tech-interview-scheduled': 'Tech planifié',
        'tech-interview-done': 'Tech fait',
        'hired': 'Recruté',
        'rejected': 'Rejeté',
        'candidate-declined': 'Décliné',
      };
      return map[status] || status.replace(/-/g, ' ');
    },
    async duplicateBoard(col) {
      const newName = prompt(`Nom du board dupliqué:`, `${col.label} (copie)`);
      if (!newName?.trim()) return;
      try {
        await axios.post(`/api/v1/my-board/${encodeURIComponent(col.id)}/duplicate`, { new_label: newName.trim() });
        this.loadBoard();
        this.loadFavoriteGroups();
      } catch (e) { console.error('Duplicate failed:', e); }
    },
    toggleColColor(colId) {
      this.colColorOpen = this.colColorOpen === colId ? null : colId;
    },
    async setColColor(col, color) {
      col.color = color;
      this.colColorOpen = null;
      // Persist if it's a custom board
      try { await axios.post('/api/v1/my-board/color', { board_id: col.id, color }); } catch (e) { /**/ }
    },
    onColDragStart(e, idx) {
      this.dragColIndex = idx;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', 'col-' + idx);
    },
    onColDragEnd() { this.dragColIndex = null; },
    formatShortDate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    },
    getBoardLabel(boardId) {
      if (boardId === 'global') return 'Global';
      if (boardId.startsWith('custom:')) return boardId.replace('custom:', '');
      const col = this.boardColumns.find(c => c.id === boardId);
      if (col) return col.label;
      const proj = this.agentProjects.find(p => p.project_id === boardId);
      return proj?.project_name || boardId.slice(0, 8);
    },
    onColSearch(col) {
      clearTimeout(this.colSearchDebounce[col.id]);
      const q = (this.colSearch[col.id] || '').trim();
      if (!q) { this.colSearchResults[col.id] = null; return; }
      if (q.length < 2) return;
      this.colSearchDebounce[col.id] = setTimeout(() => this.runColSearch(col), 300);
    },

  
 
    async runColSearch(col) {
      const q = (this.colSearch[col.id] || '').trim();
      if (!q || q.length < 2) return;
      const esIds = col.cards.map(c => c.elasticsearch_id);
      if (!esIds.length) return;
      this.colSearchLoading = { ...this.colSearchLoading, [col.id]: true };
      try {
        const res = await axios.get('/api/v1/combined_search/', {
          params: { query: q, candidate_id: esIds.join(','), limit: esIds.length, boolean_mode: false }
        });
        const matchedIds = new Set(
          (res.data.results || []).map(r => r.UID?.candidate_id || r.metadata?.candidate_id).filter(Boolean)
        );
        const lower = q.toLowerCase();
        for (const card of col.cards) {
          const localHay = [card.sticker, card.comment_text || '', ...(card.labels || []).map(l => l.label)].filter(Boolean).join(' ').toLowerCase();
          if (localHay.includes(lower)) matchedIds.add(card.elasticsearch_id);
        }
        this.colSearchResults = { ...this.colSearchResults, [col.id]: matchedIds };
      } catch (e) { console.error('Col search failed:', e); }
      finally { this.colSearchLoading = { ...this.colSearchLoading, [col.id]: false }; }
    },
    getColCards(col) {
      const resultSet = this.colSearchResults[col.id];
      if (!resultSet) return col.cards;  // no active search
      return col.cards.filter(c => resultSet.has(c.elasticsearch_id));
    },
    onAddCardSearch() {
      clearTimeout(this.addCardDebounce);
      if (this.addCardQuery.trim().length < 3) { this.addCardResults = []; return; }
      this.addCardDebounce = setTimeout(() => this.searchCandidatesForBoard(), 300);
    },

    async searchCandidatesForBoard() {
      const q = this.addCardQuery.trim();
      if (q.length < 3) return;
      this.addCardLoading = true;
      try {
        const params = { limit: 8 };
        // Detect if it's a phone number (4+ digits)
        const digitsOnly = q.replace(/\D/g, '');
        if (digitsOnly.length >= 4) {
          params.candidate_phone = digitsOnly;
        } else if (q.includes('@')) {
          params.candidate_email = q;
        } else {
          params.candidate_name = q;
        }
        const res = await axios.get('/api/v1/combined_search/', { params });
        this.addCardResults = (res.data.results || []).map(r => ({
          candidate_id: r.UID?.candidate_id || r.metadata?.candidate_id,
          name: r.name || 'Unknown',
          title: r.current_job?.title || r.work_experience?.[0]?.job_title || '',
        }));
      } catch (e) { this.addCardResults = []; }
      finally { this.addCardLoading = false; }
    },

    async addCandidateToBoard(candidate, col) {
      try {
        // First ensure the view record exists (upsert)
        await axios.post(`/api/v1/agent-views/${candidate.candidate_id}`, {});
        // Then favorite it into this column
        await axios.post(`/api/v1/my-history/${candidate.candidate_id}/favorite`, {
          project_id: col.id
        });
        // Add to local state
        col.cards.push({
          elasticsearch_id: candidate.candidate_id,
          name: candidate.name,
          title: candidate.title,
          location: '',
          labels: [],
          comment_count: 0,
          sticker: null,
        });
        this.addCardCol = null;
        this.addCardQuery = '';
        this.addCardResults = [];
      } catch (e) { console.error('Add to board failed:', e); }
    },

    async toggleBoardNotes(col) {
      if (this.boardNoteColumn === col.id) { this.boardNoteColumn = null; return; }
      this.boardNoteColumn = col.id;
      try { const res = await axios.get(`/api/v1/my-board/${encodeURIComponent(col.id)}/notes`); this.boardNotes = res.data.notes || []; }
      catch (e) { this.boardNotes = []; }
    },
    async addBoardNote(boardId) {
      if (!this.newBoardNote.trim()) return;
      try {
        const res = await axios.post(`/api/v1/my-board/${encodeURIComponent(boardId)}/note`, { text: this.newBoardNote.trim() });
        this.boardNotes.unshift({ id: res.data.id, text: this.newBoardNote.trim(), created_at: res.data.created_at });
        this.newBoardNote = '';
        const col = this.boardColumns.find(c => c.id === boardId);
        if (col) col.note_count = (col.note_count || 0) + 1;
      } catch (e) { /**/ }
    },
    async deleteBoardNote(noteId, boardId) {
      try { await axios.delete(`/api/v1/my-board/note/${noteId}`);
        this.boardNotes = this.boardNotes.filter(n => n.id !== noteId);
        const col = this.boardColumns.find(c => c.id === boardId);
        if (col) col.note_count = Math.max(0, (col.note_count || 1) - 1);
      } catch (e) { /**/ }
    },
    // ─── History Methods (unchanged) ───
    async loadHistory() {
      this.isLoading = true;
      try {
        const skip = (this.currentPage - 1) * this.pageSize;
        const params = { skip, limit: this.pageSize };
        if (this.searchTerm.trim()) params.search = this.searchTerm.trim();
        if (this.activeFilter) params.favorite_filter = this.activeFilter;
        const res = await axios.get('/api/v1/my-history', { params });
        this.days = res.data.days || [];
        this.total = res.data.total || 0;
      } catch (e) { console.error('Failed to load history:', e); }
      finally { this.isLoading = false; }
    },
    async loadFavoriteGroups() {
      try { const res = await axios.get('/api/v1/my-history/favorite-groups'); this.favoriteGroups = res.data.groups || []; } catch (e) { /* */ }
    },
    async loadAgentProjects() {
      try { const res = await axios.get('/api/v1/agent/my-projects'); this.agentProjects = (res.data.projects || []).filter(p => !p.is_hot_list); } catch (e) { /* */ }
    },
    onSearchInput() { clearTimeout(this.searchDebounce); this.searchDebounce = setTimeout(() => { this.currentPage = 1; this.loadHistory(); }, 300); },
    toggleFavDropdown(esId) { this.favDropdownOpen = this.favDropdownOpen === esId ? null : esId; },
    closeFavDropdown(e) { if (!e.target.closest('.fav-wrapper')) this.favDropdownOpen = null; },
    async setFavorite(entry, groupId) {
      try { await axios.post(`/api/v1/my-history/${entry.elasticsearch_id}/favorite`, { project_id: groupId }); entry.favorite_project_id = groupId; this.favDropdownOpen = null; this.loadFavoriteGroups(); } catch (e) { /**/ }
    },
    async removeFavorite(entry) {
      try {
        const params = entry.favorite_project_id ? { board_id: entry.favorite_project_id } : {};
        await axios.delete(`/api/v1/my-history/${entry.elasticsearch_id}/favorite`, { params });
      }
      catch (e) { /**/ }
    },
    createGroup() {
      const name = this.newGroupName.trim(); if (!name) return;
      const groupId = `custom:${name}`;
      if (!this.favoriteGroups.some(g => g.id === groupId)) this.favoriteGroups.push({ id: groupId, label: name, type: 'custom' });
      this.showCreateGroup = false; this.newGroupName = '';
    },
    getFavClass(favId) { if (favId === 'global') return 'fav-global'; if (favId?.startsWith('custom:')) return 'fav-custom'; return 'fav-project'; },
    getFavLabel(entry) {
      const fid = entry.favorite_project_id; if (!fid) return '';
      if (fid === 'global') return 'Global'; if (fid.startsWith('custom:')) return fid.replace('custom:', '');
      const proj = this.agentProjects.find(p => p.project_id === fid); return proj?.project_name || fid.slice(0, 8);
    },
    async archiveEntry(entry) {
      try { await axios.post(`/api/v1/my-history/${entry.elasticsearch_id}/archive`);
        this.days = this.days.map(d => ({ ...d, views: d.views.filter(v => v.elasticsearch_id !== entry.elasticsearch_id) })).filter(d => d.views.length > 0);
        this.total = Math.max(0, this.total - 1); } catch (e) { /**/ }
    },
    goToCandidate(esId) { this.$router.push({ path: '/', query: { candidate_id: esId, auto_search: 'true' } }); },
    formatDayLabel(dateStr) {
      const d = new Date(dateStr + 'T00:00:00'), today = new Date(), yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
      if (d.toDateString() === yesterday.toDateString()) return 'Hier';
      return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    },
    formatTime(iso) { if (!iso) return ''; return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); },
    formatCommentDate(iso) { if (!iso) return ''; const d = new Date(iso); return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); },
    prevPage() { if (this.currentPage > 1) { this.currentPage--; this.loadHistory(); } },
    nextPage() { if (this.currentPage < this.totalPages) { this.currentPage++; this.loadHistory(); } },

    // ─── Board Methods ───
    async loadBoard() {
      this.boardLoading = true;
      try { const res = await axios.get('/api/v1/my-board'); this.boardColumns = res.data.columns || []; }
      catch (e) { console.error('Board load failed:', e); }
      finally { this.boardLoading = false; }
    },
   
    async createGroupAndRefreshBoard() {
      const name = this.newGroupName.trim();
      if (!name) return;
      const groupId = `custom:${name}`;
      try {
        await axios.post('/api/v1/my-board/create', { label: name, board_type: 'custom', color: '#a855f7' });
      } catch (e) { /* silent — board will appear on reload */ }
      if (!this.favoriteGroups.some(g => g.id === groupId)) {
        this.favoriteGroups.push({ id: groupId, label: name, type: 'custom' });
      }
      this.boardAddingColumn = false;
      this.newGroupName = '';
      this.loadBoard();
    },
    // Drag & Drop
    onDragStart(e, card, col) {
      this.dragCard = card; this.dragSourceCol = col;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', card.elasticsearch_id);
    },
    onDragEnd() { this.dragCard = null; this.dragSourceCol = null; document.querySelectorAll('.drag-over').forEach(el => el.classList.remove('drag-over')); },
    onDragOver(e, col) { e.currentTarget.classList.add('drag-over'); },
    onDragLeave(e) { if (!e.currentTarget.contains(e.relatedTarget)) e.currentTarget.classList.remove('drag-over'); },
    async onDrop(e, targetCol) {
      e.currentTarget.classList.remove('drag-over');

      // Column reorder
      if (this.dragColIndex !== null) {
        const targetIdx = this.boardColumns.findIndex(c => c.id === targetCol.id);
        if (targetIdx !== -1 && this.dragColIndex !== targetIdx) {
          const moved = this.boardColumns.splice(this.dragColIndex, 1)[0];
          this.boardColumns.splice(targetIdx, 0, moved);
          // Persist order
          const ids = this.boardColumns.map(c => c.id);
          axios.post('/api/v1/my-board/reorder', ids).catch(() => {});
        }
        this.dragColIndex = null;
        return;
      }

      // Card copy
    // Card copy
    if (!this.dragCard || !this.dragSourceCol || targetCol.id === this.dragSourceCol.id) return;
      const card = this.dragCard;

      if (targetCol.scope === 'project') {
        // Copy to project board via dedicated endpoint
        if (!targetCol.cards.some(c => c.elasticsearch_id === card.elasticsearch_id)) {
          targetCol.cards.unshift({ ...card, added_by: this.currentUserId?.slice(0, 8) });
        }
        try {
          await axios.post('/api/v1/my-board/copy-to-project', {
            board_id: targetCol.id,
            elasticsearch_ids: [card.elasticsearch_id]
          });
        } catch (e) { console.error('Copy to project failed:', e); this.loadBoard(); }
      } else {
        // Normal personal board copy
        if (!targetCol.cards.some(c => c.elasticsearch_id === card.elasticsearch_id)) {
          targetCol.cards.unshift({ ...card });
        }
        try { await axios.post(`/api/v1/my-history/${card.elasticsearch_id}/favorite`, { project_id: targetCol.id }); }
        catch (e) { console.error('Copy failed:', e); this.loadBoard(); }
      }
    },

    // ─── Card Modal ───
    // async openCardModal(card, col) {
    //   this.modalCard = { ...card }; this.modalColumn = col;
    //   this.showLabelPicker = false; this.showMovePicker = false; this.newComment = '';
    //   try { const res = await axios.get(`/api/v1/my-board/${card.elasticsearch_id}/comments`); this.modalComments = res.data.comments || []; } catch (e) { this.modalComments = []; }
    //   // lazy load 
    //   try {
    //     const res = await axios.get(`/api/v1/candidates/${card.elasticsearch_id}/project-history`);
    //     this.modalProjectHistory = res.data.projects_summary || [];
    //   } catch (e) { this.modalProjectHistory = []; }
    //   finally { this.modalProjectHistoryLoading = false; }
    // },
    async openCardModal(card, col) {
      this.modalCard = { ...card }; this.modalColumn = col;
      this.showLabelPicker = false; this.showMovePicker = false; this.showProjectLinkPicker = false;
      this.newComment = '';
      this.modalProjectHistory = null;
      this.modalProjectHistoryLoading = true;
      this.modalReport = null;
      this.modalReportProject = null;
      this.modalReportLoading = false;
      const scope = col.scope || 'personal';
      try { const res = await axios.get(`/api/v1/my-board/${card.elasticsearch_id}/comments`, { params: { scope } }); this.modalComments = res.data.comments || []; } catch (e) { this.modalComments = []; }      try {
        const res = await axios.get(`/api/v1/candidates/${card.elasticsearch_id}/project-history`);
        this.modalProjectHistory = res.data.projects_summary || [];
      } catch (e) { this.modalProjectHistory = []; }
      finally { this.modalProjectHistoryLoading = false; }
    },
    closeModal() { this.modalCard = null; this.modalColumn = null; this.modalComments = []; },
    async addComment() {
      if (!this.newComment.trim()) return;
      try {
        const scope = this.modalColumn?.scope || 'personal';
        const res = await axios.post(`/api/v1/my-board/${this.modalCard.elasticsearch_id}/comment`, { text: this.newComment.trim(), scope });
        this.modalComments.unshift({ id: res.data.id, text: this.newComment.trim(), created_at: res.data.created_at, agent_name: res.data.agent_name || '', agent_id: this.currentUserId });
        this.newComment = '';
        // Update card count
        const card = this.findBoardCard(this.modalCard.elasticsearch_id);
        if (card) card.comment_count = (card.comment_count || 0) + 1;
        this.modalCard.comment_count = (this.modalCard.comment_count || 0) + 1;
      } catch (e) { console.error('Comment failed:', e); }
    },
    async deleteComment(id) {
      try { await axios.delete(`/api/v1/my-board/comment/${id}`); this.modalComments = this.modalComments.filter(c => c.id !== id);
        const card = this.findBoardCard(this.modalCard.elasticsearch_id);
        if (card) card.comment_count = Math.max(0, (card.comment_count || 1) - 1);
        this.modalCard.comment_count = Math.max(0, (this.modalCard.comment_count || 1) - 1);
      } catch (e) { /**/ }
    },
    async addLabel(preset) {
      if (this.modalCard.labels?.some(l => l.label === preset.label)) return;
      const scope = this.modalColumn?.scope || 'personal';
        
      try { await axios.post(`/api/v1/my-board/${this.modalCard.elasticsearch_id}/label`, { label: preset.label, color: preset.color, scope });
        if (!this.modalCard.labels) this.modalCard.labels = [];
        this.modalCard.labels.push({ label: preset.label, color: preset.color });
        const card = this.findBoardCard(this.modalCard.elasticsearch_id);
        if (card) card.labels = [...(this.modalCard.labels)];
      } catch (e) { /**/ }
    },
    async addCustomLabel() {
      if (!this.customLabelText.trim()) return;
      await this.addLabel({ label: this.customLabelText.trim(), color: this.customLabelColor });
      this.customLabelText = '';
    },
    async removeLabel(labelName) {
      const scope = this.modalColumn?.scope || 'personal';
       
      try {  await axios.delete(`/api/v1/my-board/${this.modalCard.elasticsearch_id}/label/${encodeURIComponent(labelName)}`, { params: { scope } });
        this.modalCard.labels = this.modalCard.labels.filter(l => l.label !== labelName);
        const card = this.findBoardCard(this.modalCard.elasticsearch_id);
        if (card) card.labels = [...(this.modalCard.labels)];
      } catch (e) { /**/ }
    },
    async setSticker(sticker) {
      try { await axios.post(`/api/v1/my-board/${this.modalCard.elasticsearch_id}/sticker`, { sticker });
        this.modalCard.sticker = sticker;
        const card = this.findBoardCard(this.modalCard.elasticsearch_id);
        if (card) card.sticker = sticker;
      } catch (e) { /**/ }
    },
    async moveCard(colId) {
      if (!this.modalCard) return;
      try { await axios.post(`/api/v1/my-history/${this.modalCard.elasticsearch_id}/favorite`, { project_id: colId }); this.showMovePicker = false; this.closeModal(); this.loadBoard(); } catch (e) { /**/ }
    },
  async removeFromBoard() {
      if (!this.modalCard || !this.modalColumn) return;
      const scope = this.modalColumn.scope || 'personal';
      if (scope === 'project' && !this.isAdmin) {
        alert('Seul un admin peut retirer un candidat du board projet.');
        return;
      }
      try {
        await axios.delete(`/api/v1/my-history/${this.modalCard.elasticsearch_id}/favorite`, {
          params: { board_id: this.modalColumn.id, scope }
        });
        this.closeModal(); this.loadBoard();
      } catch (e) { console.error('Remove failed:', e); }
    },
    findBoardCard(esId) {
      for (const col of this.boardColumns) { const c = col.cards.find(c => c.elasticsearch_id === esId); if (c) return c; }
      return null;
    },
  }
};
</script>

<style scoped>
/* ═══ Light theme text fixes ═══ */
:root[data-theme="light"] .personal-space .tab-switcher button { color: var(--text-color); opacity: 0.4; }
:root[data-theme="light"] .personal-space .tab-switcher button.active { color: #3b82f6; opacity: 1; }
:root[data-theme="light"] .personal-space .filter-chip { color: var(--text-color); opacity: 0.5; }
:root[data-theme="light"] .personal-space .filter-chip.active { opacity: 1; }
:root[data-theme="light"] .personal-space .entry-time { color: var(--text-color); opacity: 0.35; }
:root[data-theme="light"] .personal-space .entry-title { color: var(--text-color); opacity: 0.5; }
:root[data-theme="light"] .personal-space .col-count { color: var(--text-color); opacity: 0.3; }
:root[data-theme="light"] .personal-space .card-title { color: var(--text-color); opacity: 0.45; }
:root[data-theme="light"] .personal-space .card-meta { color: var(--text-color); opacity: 0.35; }
:root[data-theme="light"] .personal-space .card-icon-badge { color: var(--text-color); opacity: 0.35; }
:root[data-theme="light"] .personal-space .fav-option { color: var(--text-color); opacity: 0.6; }
:root[data-theme="light"] .personal-space .modal-header-info h3 { color: var(--text-color); }
:root[data-theme="light"] .personal-space .modal-subtitle { color: var(--text-color); opacity: 0.5; }
:root[data-theme="light"] .personal-space .modal-section h4 { color: var(--text-color); opacity: 0.5; }
:root[data-theme="light"] .personal-space .action-btn { color: var(--text-color); opacity: 0.65; }
:root[data-theme="light"] .personal-space .comment-text { color: var(--text-color); opacity: 0.7; }
:root[data-theme="light"] .personal-space .comment-meta span { color: var(--text-color); opacity: 0.3; }
:root[data-theme="light"] .personal-space .board-note-text { color: var(--text-color); opacity: 0.6; }
:root[data-theme="light"] .personal-space .col-add-btn-inline { color: var(--text-color); opacity: 0.3; }
:root[data-theme="light"] .personal-space .search-help-tip { color: var(--text-color); opacity: 0.3; }
:root[data-theme="light"] .personal-space .search-help-tip b { color: var(--text-color); opacity: 0.5; }
:root[data-theme="light"] .personal-space .card-modal { background: var(--background-color); }
:root[data-theme="light"] .personal-space .fav-dropdown { background: var(--background-color); }
:root[data-theme="light"] .personal-space .empty-state { color: var(--text-color); opacity: 0.4; }
:root[data-theme="light"] .personal-space .loading-state { color: var(--text-color); opacity: 0.4; }
:root[data-theme="light"] .personal-space .search-input::placeholder,
:root[data-theme="light"] .personal-space .board-search-input::placeholder,
:root[data-theme="light"] .personal-space .comment-input::placeholder,
:root[data-theme="light"] .personal-space .col-search-input::placeholder,
:root[data-theme="light"] .personal-space .add-card-input::placeholder,
:root[data-theme="light"] .personal-space .board-note-input::placeholder { color: var(--text-color); opacity: 0.3; }
/* ═══ Theme-safe root override ═══ */
.personal-space {
  color: var(--text-color);
}
.personal-space .history-search .search-input,
.personal-space .board-search-input,
.personal-space .comment-input,
.personal-space .group-input,
.personal-space .add-col-input,
.personal-space .label-custom-input {
  background: var(--surface-color);
  border-color: var(--border-color);
  color: var(--text-color);
}
.personal-space .history-entry:hover,
.personal-space .board-card:hover {
  background:  var(--card-tint-hover, var(--hover-color));
}
.personal-space .board-column {
  background: var(--col-tint, var(--surface-color));
  border-color: var(--col-tint-border, var(--border-color));
}
.personal-space .board-card {
  background: var(--card-tint, var(--background-color));
  border-color: var(--card-tint-border, var(--border-color));
}

.personal-space .card-modal {
  background: var(--background-color);
  border-color: var(--border-color);
}
.personal-space .fav-dropdown {
  background: var(--background-color);
  border-color: var(--border-color);
}
.personal-space .tab-switcher {
  background: var(--surface-color);
  border-color: var(--border-color);
}
.personal-space .filter-chip {
  border-color: var(--border-color);
  background: var(--surface-color);
}
/* ═══════════════════════════════════════════ */
/* ═══ PERSONAL SPACE — ROOT              ═══ */
/* ═══════════════════════════════════════════ */
.personal-space { padding: 20px 16px; color: var(--text-color); min-height: 100vh; }

/* Header */
.space-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; max-width: 1400px; margin-left: auto; margin-right: auto; }
.header-left h2 { font-size: 1.3rem; font-weight: 800; color: rgba(250,249,246,0.92); display: flex; align-items: center; gap: 10px; letter-spacing: -0.02em; }
.header-left h2 i { color: #93c5fd; }
.tab-switcher { display: flex; background: var(--surface-color); border: 1px solid rgba(250,249,246,0.06); border-radius: 10px; overflow: hidden; }
.tab-switcher button { padding: 8px 20px; background: none; border: none; color: rgba(250,249,246,0.4); font-size: 0.78rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all 0.2s; }
.tab-switcher button.active { background: rgba(59,130,246,0.12); color: #93c5fd; }
.tab-switcher button:hover:not(.active) { color: rgba(250,249,246,0.7); }

/* ═══ HISTORY TAB ═══ */
.history-tab { max-width: 900px; margin: 0 auto; }

/* Filter chips */
.favorite-filters { display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: 12px; }
.filter-chip { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 6px; border: 1px solid var(--border-color); background: var(--surface-color); opacity: 0.6; color: rgba(250,249,246,0.4); font-size: 0.68rem; font-weight: 600; cursor: pointer; transition: all 0.15s; }
.filter-chip:hover { background: rgba(250,249,246,0.05); color: rgba(250,249,246,0.7); }
.filter-chip.active { background: rgba(59,130,246,0.12); border-color: rgba(59,130,246,0.25); color: #93c5fd; }
.filter-chip.fav-all.active { background: rgba(234,179,8,0.1); border-color: rgba(234,179,8,0.25); color: #fbbf24; }
.filter-chip.chip-project.active { background: rgba(245,158,11,0.1); border-color: rgba(245,158,11,0.3); color: #f59e0b; }
.filter-chip.chip-custom.active { background: rgba(168,85,247,0.1); border-color: rgba(168,85,247,0.3); color: #a855f7; }
.filter-chip.chip-global.active { background: rgba(34,197,94,0.1); border-color: rgba(34,197,94,0.25); color: #22c55e; }
.filter-chip.chip-add { border-style: dashed; color: rgba(250,249,246,0.2); }
.filter-chip i { font-size: 0.6rem; }

/* Create group bar */
.create-group-bar { display: flex; gap: 6px; margin-bottom: 12px; }
.group-input { flex: 1; padding: 6px 10px; background: var(--surface-color); border: 1px solid rgba(168,85,247,0.2); border-radius: 6px; color: var(--text-color); font-size: 0.75rem; outline: none; }
.group-input::placeholder { color: rgba(250,249,246,0.25); }
.group-save-btn, .group-cancel-btn { padding: 5px 8px; border-radius: 5px; border: none; cursor: pointer; font-size: 0.7rem; }
.group-save-btn { background: rgba(168,85,247,0.15); color: #a855f7; }
.group-save-btn:disabled { opacity: 0.3; }
.group-cancel-btn { background: var(--surface-color); color: rgba(250,249,246,0.4); }

/* Search */
.history-search { position: relative; margin-bottom: 20px; }
.search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: rgba(250,249,246,0.25); font-size: 0.75rem; }
.search-input { width: 100%; padding: 9px 32px 9px 34px; background: var(--surface-color); border: 1px solid var(--border-color); border-radius: 8px; color: var(--text-color); font-size: 0.78rem; outline: none; box-sizing: border-box; }
.search-input::placeholder { color: rgba(250,249,246,0.25); }
.search-input:focus { border-color: rgba(59,130,246,0.3); }
.search-clear { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color: rgba(250,249,246,0.3); cursor: pointer; font-size: 0.7rem; }

/* Loading / Empty */
.loading-state, .empty-state { text-align: center; padding: 60px 20px; color: rgba(250,249,246,0.35); }
.loading-spinner { width: 28px; height: 28px; border: 2px solid rgba(250,249,246,0.1); border-top-color: #93c5fd; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 12px; }
@keyframes spin { to { transform: rotate(360deg); } }
.spinning { animation: spin 1s linear infinite; }
.empty-state i { font-size: 2rem; margin-bottom: 12px; }
.empty-state h3 { color: rgba(250,249,246,0.5); margin-bottom: 6px; }

/* Day groups */
.day-group { margin-bottom: 20px; }
.day-header { display: flex; justify-content: space-between; align-items: center; padding: 6px 0; margin-bottom: 6px; border-bottom: 1px solid var(--border-color); }
.day-label { font-size: 0.78rem; font-weight: 700; color: var(--text-color); text-transform: capitalize; }
.day-count { font-size: 0.65rem; color: var(--text-color); font-weight: 600; }

/* Entries */
.day-entries { display: flex; flex-direction: column; gap: 2px; }
.history-entry { display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; border-radius: 8px; transition: background 0.15s; }
.history-entry:hover { background: var(--surface-color); }
.entry-left { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; cursor: pointer; }
.entry-time { font-size: 0.65rem; color: rgba(250,249,246,0.25); font-weight: 700; font-variant-numeric: tabular-nums; width: 42px; flex-shrink: 0; }
.entry-details { display: flex; flex-direction: column; min-width: 0; }
.entry-name { font-size: 0.8rem; font-weight: 600; color: var(--text-color); opacity: 0.9; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.entry-title { font-size: 0.68rem; color: var(--text-color); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.entry-right { display: flex; align-items: center; gap: 5px; flex-shrink: 0; max-width: 60%; }
.fav-badge { display: inline-flex; align-items: center; gap: 3px; font-size: 0.58rem; font-weight: 700; padding: 1px 6px; border-radius: 4px; white-space: nowrap; }
.fav-badge i { font-size: 0.5rem; }
.fav-global { color: #22c55e; background: rgba(34,197,94,0.08); border: 1px solid rgba(34,197,94,0.15); }
.fav-project { color: #f59e0b; background: rgba(245,158,11,0.06); border: 1px solid rgba(245,158,11,0.12); }
.fav-custom { color: #a855f7; background: rgba(168,85,247,0.06); border: 1px solid rgba(168,85,247,0.12); }
.entry-project { display: inline-flex; align-items: center; gap: 4px; font-size: 0.62rem; font-weight: 700; color: #f59e0b; background: rgba(245,158,11,0.06); border: 1px solid rgba(245,158,11,0.12); padding: 2px 8px; border-radius: 5px; white-space: nowrap; }
.entry-project i { font-size: 0.55rem; }
.entry-query { display: inline-flex; align-items: center; gap: 4px; font-size: 0.62rem; font-weight: 600; color: var(--text-color); background: var(--surface-color); opacity: 0.6; border: 1px solid rgba(250,249,246,0.06); padding: 2px 8px; border-radius: 5px; max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; cursor: default; transition: max-width 0.3s; }
.entry-query i { font-size: 0.55rem; flex-shrink: 0; }
.entry-query.expanded { max-width: 500px; white-space: normal; word-break: break-word; }
.fav-wrapper { position: relative; }
.fav-btn { background: none; border: none; color: rgba(250,249,246,0.0); cursor: pointer; padding: 4px; font-size: 0.7rem; border-radius: 4px; transition: all 0.15s; }
.history-entry:hover .fav-btn { color: rgba(250,249,246,0.2); }
.fav-btn.active { color: #fbbf24 !important; }
.fav-btn:hover { color: #fbbf24 !important; }
.fav-dropdown { position: absolute; right: 0; top: 100%; z-index: 100; background: #1a1a2e; border: 1px solid var(--border-color)
; border-radius: 8px; min-width: 180px; max-height: 280px; overflow-y: auto; padding: 4px 0; box-shadow: 0 8px 24px rgba(0,0,0,0.4); }
.fav-option { display: flex; align-items: center; gap: 6px; width: 100%; padding: 7px 12px; border: none; background: none; color: rgba(250,249,246,0.6); font-size: 0.72rem; font-weight: 600; cursor: pointer; text-align: left; }
.fav-option:hover { background: var(--surface-color); color: rgba(250,249,246,0.9); }
.fav-option.fav-remove { color: #f87171; }
.fav-divider { height: 1px; background: rgba(250,249,246,0.06); margin: 2px 0; }
.archive-btn { background: none; border: none; color: rgba(250,249,246,0.0); cursor: pointer; padding: 4px; font-size: 0.65rem; border-radius: 4px; transition: all 0.15s; }
.history-entry:hover .archive-btn { color: rgba(250,249,246,0.2); }
.archive-btn:hover { color: #f87171 !important; background: rgba(248,113,113,0.08); }
.pagination { display: flex; justify-content: center; align-items: center; gap: 12px; margin-top: 20px; }
.page-btn { background: var(--surface-color); border: 1px solid var(--border-color); border-radius: 6px; color: rgba(250,249,246,0.5); padding: 5px 10px; cursor: pointer; font-size: 0.7rem; }
.page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

/* ═══════════════════════════════════════════ */
/* ═══ BOARD TAB — TRELLO KANBAN          ═══ */
/* ═══════════════════════════════════════════ */
.board-tab { width: 100%; overflow: hidden; }
.board-canvas { display: flex; gap: 14px; overflow-x: auto; padding: 4px 4px 20px; min-height: 60vh; align-items: flex-start; scroll-behavior: smooth; scrollbar-width: thin; scrollbar-color: rgba(250,249,246,0.08) transparent; }

/* Column */
.board-column { min-width: 280px; max-width: 280px; background: var(--col-tint, var(--surface-color)); border: 1px solid var(--col-tint-border, var(--border-color)); border-radius: 12px; display: flex; flex-direction: column; max-height: calc(100vh - 160px); transition: border-color 0.15s, box-shadow 0.15s; }
.board-column.drag-over { border-color: rgba(59,130,246,0.4); box-shadow: 0 0 0 2px rgba(59,130,246,0.15); }

.col-header { padding: 12px 14px 8px; display: flex; justify-content: space-between; align-items: center; }
.col-header-left { display: flex; align-items: center; gap: 8px; }
.col-dot { width: 8px; height: 8px; border-radius: 3px; flex-shrink: 0; }
.col-title { font-size: 0.82rem; font-weight: 800; color: var(--text-color); opacity: 0.9; letter-spacing: -0.01em; }
.col-count { font-size: 0.6rem; font-weight: 700; color: rgba(250,249,246,0.25); background: var(--surface-color); padding: 1px 6px; border-radius: 4px; }

.col-cards { flex: 1; overflow-y: scroll; max-height: 400px;  padding: 4px 8px; display: flex; flex-direction: column; gap: 6px; scrollbar-width: thin; scrollbar-color: var(--text-color); }

.col-cards::-webkit-scrollbar { width: 4px; }
.col-cards::-webkit-scrollbar-track { background: transparent; }
.col-cards::-webkit-scrollbar-thumb { background: var(--border-color); border-radius: 4px; }
.col-cards { scrollbar-width: thin; scrollbar-color: var(--border-color) transparent; }
 
/* Card */ 
.board-card { background: var(--surface-color); border: 1px solid rgba(250,249,246,0.06); border-radius: 8px; padding: 0; cursor: grab; transition: all 0.15s; position: relative;   }
.board-card:hover { background: rgba(250,249,246,0.06); border-color: rgba(250,249,246,0.1); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.2); }


.board-card.dragging { opacity: 0.3; transform: rotate(2deg); }
.board-card:active { cursor: grabbing; }

.card-labels { display: flex; gap: 3px; padding: 6px 10px 0; }
.card-label-bar { height: 4px; flex: 1; border-radius: 2px; max-width: 40px; }
.card-sticker { position: absolute; top: 4px; right: 8px; font-size: 1rem; }

.card-name { font-size: 0.78rem; font-weight: 700; color: var(--text-color); padding: 8px 10px 2px; line-height: 1.3; }
.card-title { font-size: 0.65rem; color: rgba(250,249,246,0.35); padding: 0 10px; }
.card-footer { display: flex; justify-content: space-between; align-items: center; padding: 6px 10px 8px; }
.card-meta { font-size: 0.58rem; color: rgba(250,249,246,0.25); display: flex; align-items: center; gap: 3px; }
.card-meta i { font-size: 0.5rem; }
.card-icons { display: flex; gap: 6px; }
.card-icon-badge { font-size: 0.58rem; color: rgba(250,249,246,0.25); display: flex; align-items: center; gap: 2px; }
.card-icon-badge i { font-size: 0.5rem; }

.col-add-hint { padding: 8px 14px 12px; font-size: 0.65rem; color: rgba(250,249,246,0.15); display: flex; align-items: center; gap: 4px; }

/* Add Column */
.add-column { min-width: 280px; max-width: 280px; }
.add-col-btn { background: var(--surface-color); opacity: 0.6; border: 1px dashed rgba(250,249,246,0.08); border-radius: 12px; padding: 14px; display: flex; align-items: center; gap: 6px; color: rgba(250,249,246,0.25); font-size: 0.78rem; font-weight: 600; cursor: pointer; transition: all 0.15s; }
.add-col-btn:hover { background: var(--surface-color); color: rgba(250,249,246,0.5); border-color: rgba(250,249,246,0.15); }
.add-col-form { background: var(--surface-color); border: 1px solid var(--border-color); border-radius: 12px; padding: 10px; }
.add-col-input { width: 100%; padding: 8px; background: var(--surface-color); border: 1px solid var(--border-color)
; border-radius: 6px; color: var(--text-color); font-size: 0.78rem; outline: none; margin-bottom: 8px; box-sizing: border-box; }
.add-col-actions { display: flex; gap: 6px; }
.add-col-save { padding: 6px 14px; background: rgba(59,130,246,0.15); border: none; border-radius: 6px; color: #93c5fd; font-size: 0.72rem; font-weight: 700; cursor: pointer; }
.add-col-save:disabled { opacity: 0.3; }
.add-col-cancel { padding: 6px 8px; background: none; border: none; color: rgba(250,249,246,0.3); cursor: pointer; font-size: 0.8rem; }

/* ═══════════════════════════════════════════ */
/* ═══ CARD MODAL                          ═══ */
/* ═══════════════════════════════════════════ */
.card-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 9999; display: flex; justify-content: center; align-items: flex-start; padding-top: 60px; backdrop-filter: blur(4px); overflow-y: auto; }
.card-modal {background: var(--background-color); border: 1px solid var(--border-color); border-radius: 14px; width: 680px; max-width: 95vw; box-shadow: 0 24px 64px rgba(0,0,0,0.5); overflow: hidden; }
 

.modal-header { padding: 20px 24px 12px; border-bottom: 1px solid rgba(250,249,246,0.05); display: flex; justify-content: space-between; }
.modal-header-info h3 { font-size: 1.1rem; font-weight: 800; color: rgba(250,249,246,0.9); margin: 0 0 4px; }
.modal-subtitle { font-size: 0.75rem;  color: var(--text-color); display: block; margin-bottom: 8px; }
.modal-col-badge { font-size: 0.65rem; font-weight: 700; padding: 2px 8px; border-radius: 4px; border: 1px solid; display: inline-flex; align-items: center; gap: 4px; }
.modal-col-badge i { font-size: 0.55rem; }
.modal-close { background: none; border: none; color: rgba(250,249,246,0.3); font-size: 1rem; cursor: pointer; padding: 4px; }
.modal-close:hover { color: rgba(250,249,246,0.7); }

.modal-body { display: flex; gap: 0; }
.modal-content { flex: 1; padding: 16px 24px 24px; min-width: 0; }
.modal-actions { width: 170px; padding: 16px 16px 24px; border-left: 1px solid rgba(250,249,246,0.05); flex-shrink: 0; }
.modal-actions h5 { font-size: 0.65rem; font-weight: 700; color: rgba(250,249,246,0.3); text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 8px; }
.action-btn { display: flex; align-items: center; gap: 6px; width: 100%; padding: 7px 10px; background: var(--surface-color); border: 1px solid rgba(250,249,246,0.06); border-radius: 6px; color: rgba(250,249,246,0.6); font-size: 0.72rem; font-weight: 600; cursor: pointer; margin-bottom: 4px; text-align: left; }
.action-btn:hover { background: rgba(250,249,246,0.06); color: rgba(250,249,246,0.9); }
.action-btn i { font-size: 0.6rem; width: 14px; }
.action-danger { color: #f87171; border-color: rgba(248,113,113,0.1); }
.action-danger:hover { background: rgba(248,113,113,0.08); }

/* Move Picker */
.move-picker { margin-bottom: 8px; }
.move-option { display: flex; align-items: center; gap: 6px; width: 100%; padding: 5px 10px; background: none; border: none; color: rgba(250,249,246,0.5); font-size: 0.68rem; font-weight: 600; cursor: pointer; border-radius: 4px; text-align: left; }
.move-option:hover { background: var(--surface-color); color: rgba(250,249,246,0.9); }
.move-option.current { color: #93c5fd; }
.col-dot-sm { width: 6px; height: 6px; border-radius: 2px; flex-shrink: 0; }

/* Sections */
.modal-section { margin-bottom: 20px; }
.modal-section h4 { font-size: 0.75rem; font-weight: 700; color: rgba(250,249,246,0.5); margin: 0 0 8px; display: flex; align-items: center; gap: 6px; }
.modal-section h4 i { font-size: 0.65rem; }

/* Labels */
.modal-labels { display: flex; flex-wrap: wrap; gap: 5px; align-items: center; }
.modal-label { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 4px; font-size: 0.68rem; font-weight: 700; color: #fff; }
.label-remove { cursor: pointer; opacity: 0.6; font-size: 0.55rem; }
.label-remove:hover { opacity: 1; }
.add-label-btn { width: 24px; height: 24px; border-radius: 4px; border: 1px dashed rgba(250,249,246,0.15); background: none; color: rgba(250,249,246,0.25); cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 0.6rem; }
.add-label-btn:hover { color: rgba(250,249,246,0.5); border-color: rgba(250,249,246,0.25); }

/* Label Picker */
.label-picker { margin-top: 8px; background: var(--surface-color); opacity: 0.6; border: 1px solid rgba(250,249,246,0.06); border-radius: 8px; padding: 8px; }
.label-preset { display: flex; align-items: center; gap: 8px; padding: 5px 8px; border-radius: 5px; cursor: pointer; font-size: 0.72rem; font-weight: 600; color: rgba(250,249,246,0.6); transition: background 0.1s; }
.label-preset:hover { background: var(--surface-color); }
.label-preset.used { opacity: 0.35; pointer-events: none; }
.label-dot { width: 12px; height: 12px; border-radius: 3px; flex-shrink: 0; }
.label-custom-row { display: flex; align-items: center; gap: 6px; margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(250,249,246,0.05); }
.label-custom-input { flex: 1; padding: 4px 8px; background: var(--surface-color); border: 1px solid var(--border-color); border-radius: 4px; color: var(--text-color); font-size: 0.7rem; outline: none; }
.label-color-dots { display: flex; gap: 3px; }
.color-dot { width: 14px; height: 14px; border-radius: 3px; cursor: pointer; border: 2px solid transparent; transition: border-color 0.1s; }
.color-dot.active { border-color: rgba(250,249,246,0.6); }
.label-add-btn { background: rgba(59,130,246,0.1); border: none; border-radius: 4px; color: #93c5fd; padding: 4px 6px; cursor: pointer; font-size: 0.65rem; }
.label-add-btn:disabled { opacity: 0.3; }

/* Stickers */
.sticker-picker { display: flex; flex-wrap: wrap; gap: 4px; }
.sticker-option { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; border-radius: 6px; cursor: pointer; border: 2px solid transparent; transition: all 0.1s; background: var(--surface-color); opacity: 0.6; }
.sticker-option:hover { background: rgba(250,249,246,0.06); transform: scale(1.15); }
.sticker-option.active { border-color: rgba(59,130,246,0.4); background: rgba(59,130,246,0.1); }
.sticker-clear { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; border-radius: 6px; cursor: pointer; color: rgba(250,249,246,0.2); font-size: 0.6rem; background: var(--surface-color); opacity: 0.6; border: none; }
.sticker-clear:hover { color: #f87171; }

/* Comments */
.comment-input-row { display: flex; gap: 6px; margin-bottom: 12px; }
.comment-input { flex: 1; padding: 8px 10px; background: var(--surface-color); border: 1px solid var(--border-color); border-radius: 8px; color: var(--text-color); font-size: 0.75rem; resize: vertical; min-height: 36px; outline: none; font-family: inherit; }
.comment-input::placeholder { color: rgba(250,249,246,0.2); }
.comment-input:focus { border-color: rgba(59,130,246,0.3); }
.comment-submit { background: rgba(59,130,246,0.12); border: none; border-radius: 8px; color: #93c5fd; padding: 8px 12px; cursor: pointer; font-size: 0.75rem; align-self: flex-end; }
.comment-submit:disabled { opacity: 0.3; }
.comments-list { display: flex; flex-direction: column; gap: 6px; }
.comment-item { background: var(--surface-color); opacity: 0.6; border: 1px solid rgba(250,249,246,0.04); border-radius: 8px; padding: 8px 10px; }
.comment-text { font-size: 0.75rem; color: rgba(250,249,246,0.75); line-height: 1.4; white-space: pre-wrap; word-break: break-word; }
.comment-meta { display: flex; justify-content: space-between; align-items: center; margin-top: 4px; }
.comment-meta span { font-size: 0.6rem; color: rgba(250,249,246,0.2); }
.comment-delete { background: none; border: none; color: rgba(250,249,246,0.1); cursor: pointer; font-size: 0.55rem; padding: 2px; }
.comment-delete:hover { color: #f87171; }

/* Info */
.modal-info .info-row { display: flex; align-items: center; gap: 6px; font-size: 0.68rem; color: rgba(250,249,246,0.3); margin-bottom: 4px; }
.modal-info .info-row i { font-size: 0.6rem; width: 14px; }
.board-search-bar { position: relative; max-width: 400px; margin-bottom: 12px; }
.board-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-color); opacity: 0.25; font-size: 0.7rem; }
.board-search-input { width: 100%; padding: 8px 30px 8px 30px; background: var(--surface-color); border: 1px solid var(--border-color); border-radius: 8px; color: var(--text-color); font-size: 0.75rem; outline: none; box-sizing: border-box; }
.board-search-input::placeholder { color: var(--text-color); opacity: 0.3; }
.board-search-input:focus { border-color: rgba(59,130,246,0.35); }
.board-search-clear { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); color: var(--text-color); opacity: 0.25; cursor: pointer; font-size: 0.65rem; }
.board-search-clear:hover { opacity: 0.6; }

/* Column header right */
.col-header-right { display: flex; gap: 4px; }
.col-note-btn { background: none; border: none; color: rgba(250,249,246,0.0); cursor: pointer; font-size: 0.65rem; padding: 2px 4px; border-radius: 4px; transition: all 0.15s; }
.board-column:hover .col-note-btn { color: rgba(250,249,246,0.2); }
.col-note-btn:hover { color: rgba(250,249,246,0.5) !important; }
.col-note-badge { background: none; border: none; color: #f59e0b; cursor: pointer; font-size: 0.6rem; padding: 2px 4px; display: flex; align-items: center; gap: 2px; }
.col-note-badge i { font-size: 0.55rem; }
.col-action-btn { 
background: none;
border: none;
color: #d3d9ff;
cursor: pointer;
font-size: 0.6rem;
padding: 2px 4px;
display: flex;
align-items: center;
gap: 2px;
}
.col-action-btn i { font-size: 0.55rem; }

/* Board notes panel */
.board-notes-panel { padding: 6px 10px 8px; border-bottom: 1px solid var(--border-color); }
.board-note-input-row { display: flex; gap: 4px; margin-bottom: 6px; }
.board-note-input { flex: 1; padding: 5px 8px; background: var(--surface-color); border: 1px solid var(--border-color); border-radius: 5px; color: var(--text-color); font-size: 0.7rem; outline: none; }
.board-note-input::placeholder { color: rgba(250,249,246,0.2); }
.board-note-send { background: rgba(59,130,246,0.1); border: none; border-radius: 5px; color: #93c5fd; padding: 4px 8px; cursor: pointer; font-size: 0.6rem; }
.board-note-send:disabled { opacity: 0.3; }
.board-note-item { padding: 4px 0; border-bottom: 1px solid rgba(250,249,246,0.03); }
.board-note-text { font-size: 0.68rem; color: rgba(250,249,246,0.65); line-height: 1.3; }
.board-note-meta { display: flex; justify-content: space-between; align-items: center; margin-top: 2px; }
.board-note-meta span { font-size: 0.55rem; color: rgba(250,249,246,0.2); }
.board-note-del { background: none; border: none; color: rgba(250,249,246,0.08); cursor: pointer; font-size: 0.5rem; padding: 1px; }
.board-note-del:hover { color: #f87171; }


/* Add Card inline */
.col-add-card { padding: 4px 8px 10px; }
.col-add-btn-inline { padding: 6px 10px; border-radius: 6px; cursor: pointer; font-size: 0.68rem; font-weight: 600; color: rgba(250,249,246,0.2); display: flex; align-items: center; gap: 4px; transition: all 0.15s; }
.col-add-btn-inline:hover { background: var(--surface-color); color: rgba(250,249,246,0.5); }
.add-card-search { position: relative; }
.add-card-input { width: 100%; padding: 7px 30px 7px 10px; background: var(--surface-color); border: 1px solid var(--border-color); border-radius: 6px; color: var(--text-color); font-size: 0.72rem; outline: none; box-sizing: border-box; }
.add-card-input::placeholder { color: rgba(250,249,246,0.25); }
.add-card-input:focus { border-color: rgba(59,130,246,0.35); }
.add-card-close { position: absolute; right: 6px; top: 50%; transform: translateY(-50%); background: none; border: none; color: rgba(250,249,246,0.25); cursor: pointer; font-size: 0.6rem; }
.add-card-status { text-align: center; padding: 8px; color: rgba(250,249,246,0.3); font-size: 0.65rem; }
.add-card-results { margin-top: 4px; background: var(--background-color); border: 1px solid var(--border-color); border-radius: 6px; overflow: hidden; }
.add-card-result { padding: 7px 10px; cursor: pointer; transition: background 0.1s; display: flex; flex-direction: column; gap: 1px; }
.add-card-result:hover { background: var(--surface-color); }
.acr-name { font-size: 0.72rem; font-weight: 600; color: var(--text-color); }
.acr-title { font-size: 0.6rem; color: rgba(250,249,246,0.35); }
.add-card-empty { padding: 8px 10px; font-size: 0.65rem; color: rgba(250,249,246,0.25); text-align: center; }

.search-help-tip { display: flex; align-items: center; gap: 6px; font-size: 0.62rem; color: rgba(250,249,246,0.2); margin-top: -8px; margin-bottom: 12px; max-width: 400px; }
.search-help-tip i { font-size: 0.55rem; flex-shrink: 0; }
.search-help-tip b { color: rgba(250,249,246,0.35); }

/* In-column search */
.col-search-row { position: relative; padding: 0 2px 6px; }
.col-search-input { width: 100%; padding: 5px 24px 5px 8px; background: var(--background-color); border: 1px solid var(--border-color); border-radius: 5px; color: var(--text-color); font-size: 0.68rem; outline: none; box-sizing: border-box; opacity: 0.5; transition: opacity 0.15s; }
.col-search-input:focus { opacity: 1; border-color: rgba(59,130,246,0.3); }
.col-search-input::placeholder { color: rgba(250,249,246,0.2); }
.col-search-clear { position: absolute; right: 8px; top: 50%; transform: translateY(-60%); color: rgba(250,249,246,0.2); cursor: pointer; font-size: 0.55rem; }

/* Also-in badges */
.modal-also-in { display: flex; align-items: center; gap: 4px; margin-top: 6px; flex-wrap: wrap; }
.also-label { font-size: 0.6rem; color:var(--text-color); }
.also-badge { font-size: 0.58rem; font-weight: 700; padding: 1px 6px; border-radius: 4px; }
.also-global { color: #22c55e; background: rgba(34,197,94,0.08); border: 1px solid rgba(34,197,94,0.12); }
.also-project { color: #f59e0b; background: rgba(245,158,11,0.06); border: 1px solid rgba(245,158,11,0.1); }
.also-custom { color: #a855f7; background: rgba(168,85,247,0.06); border: 1px solid rgba(168,85,247,0.1); }

/* Column search spinner */
.col-search-spin { position: absolute; right: 8px; top: 50%; transform: translateY(-60%); color: #93c5fd; font-size: 0.55rem; }

.col-dragging { opacity: 0.4; transform: scale(0.95); }
.col-header { cursor: grab; }
.col-header:active { cursor: grabbing; }


.col-color-wrapper { position: relative; }
.col-color-trigger { color: var(--text-color); opacity: 0; cursor: pointer; font-size: 0.55rem; transition: opacity 0.15s; padding: 2px; }
.board-column:hover .col-color-trigger { opacity: 0.2; }
.col-color-trigger:hover { opacity: 0.6 !important; }
.col-color-picker { position: absolute; top: 100%; left: 0; z-index: 50; display: flex; gap: 4px; flex-wrap: wrap; width: 130px; background: var(--background-color); border: 1px solid var(--border-color); border-radius: 8px; padding: 6px; box-shadow: 0 8px 20px rgba(0,0,0,0.3); }
.col-color-dot { width: 18px; height: 18px; border-radius: 4px; cursor: pointer; border: 2px solid transparent; transition: border-color 0.1s, transform 0.1s; }
.col-color-dot:hover { transform: scale(1.15); }
.col-color-dot.active { border-color: var(--text-color); }

/* Project History in modal */
.ph-loading { font-size: 0.68rem; color: var(--text-color); opacity: 0.3; padding: 8px 0; }
.ph-loading i { margin-right: 4px; }
.ph-empty { font-size: 0.68rem; color: var(--text-color); opacity: 0.2; padding: 4px 0; }
 
.ph-list {
  max-height: 300px; overflow-y: auto; display: flex; flex-direction: column; gap: 6px;
}
.ph-list::-webkit-scrollbar { width: 4px; }
.ph-list::-webkit-scrollbar-track { background: transparent; }
.ph-list::-webkit-scrollbar-thumb { background: var(--text-color); opacity: 0.2; border-radius: 4px; }
.ph-list { scrollbar-width: thin; scrollbar-color: var(--border-color) transparent; }


.ph-item { background: var(--surface-color); border: 1px solid var(--border-color); border-radius: 8px; padding: 8px 10px; }
.ph-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; }
.ph-info { display: flex; flex-direction: column; min-width: 0; }
.ph-name { font-size: 0.72rem; font-weight: 700; color: var(--text-color); opacity: 0.85; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ph-meta { font-size: 0.6rem; color: var(--text-color); opacity: 0.35; }
.ph-status { font-size: 0.58rem; font-weight: 700; padding: 2px 7px; border-radius: 4px; white-space: nowrap; flex-shrink: 0; text-transform: uppercase; }
.ph-active { color: #22c55e; background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.15); }
.ph-completed { color: #64748b; background: rgba(100,116,139,0.08); border: 1px solid rgba(100,116,139,0.12); }
.ph-cancelled { color: #ef4444; background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.1); }
.ph-draft { color: #f59e0b; background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.12); }
.ph-workflow { margin-top: 4px; }
.ph-workflow-badge { font-size: 0.6rem; font-weight: 600; padding: 2px 8px; border-radius: 4px; display: inline-flex; align-items: center; gap: 3px; }
.phw-screened, .phw-interview-done, .phw-tech-interview-done, .phw-hired { color: #22c55e; background: rgba(34,197,94,0.08); }
.phw-screening-in-progress, .phw-interview-scheduled, .phw-interview-confirmed, .phw-tech-interview-confirmed, .phw-tech-interview-scheduled { color: #3b82f6; background: rgba(59,130,246,0.08); }
.phw-request-screening, .phw-interview-requested, .phw-tech-interview-requested { color: #f59e0b; background: rgba(245,158,11,0.08); }
.phw-rejected, .phw-candidate-declined { color: #ef4444; background: rgba(239,68,68,0.08); }
.card-pipeline { display: flex; flex-wrap: wrap; gap: 3px; padding: 2px 10px 0; }
.card-pipe-pill { font-size: 0.52rem; font-weight: 700; padding: 1px 5px; border-radius: 3px; white-space: nowrap; }
.pipe-active { color: #3b82f6; background: rgba(59,130,246,0.1); }
.pipe-pending { color: #f59e0b; background: rgba(245,158,11,0.08); }
.pipe-complete { color: #22c55e; background: rgba(34,197,94,0.08); }
.pipe-done { color: #a855f7; background: rgba(168,85,247,0.08); }
.pipe-out { color: #ef4444; background: rgba(239,68,68,0.06); }
.card-pipe-more { font-size: 0.5rem; color: var(--text-color); opacity: 0.25; }

/* Pipeline banner in modal header */
.modal-pipeline-banner { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 8px; }
.mpb-item { display: flex; align-items: center; gap: 4px; font-size: 0.6rem; font-weight: 600; padding: 2px 8px; border-radius: 4px; }
.mpb-status { font-weight: 700; }
.mpb-project { opacity: 0.6; }
.mpb-active { color: #3b82f6; background: rgba(59,130,246,0.08); border: 1px solid rgba(59,130,246,0.15); }
.mpb-pending { color: #f59e0b; background: rgba(245,158,11,0.06); border: 1px solid rgba(245,158,11,0.12); }
.mpb-complete { color: #22c55e; background: rgba(34,197,94,0.06); border: 1px solid rgba(34,197,94,0.12); }
.mpb-done { color: #a855f7; background: rgba(168,85,247,0.06); border: 1px solid rgba(168,85,247,0.1); }
.mpb-out { color: #ef4444; background: rgba(239,68,68,0.05); border: 1px solid rgba(239,68,68,0.1); }

/* Action divider and pipeline button */
.action-divider { height: 1px; background: var(--border-color); margin: 8px 0; }
.action-pipeline { border-color: rgba(59,130,246,0.2); color: #93c5fd; }
.action-pipeline:hover { background: rgba(59,130,246,0.08); }
.action-blocked { display: flex; align-items: center; gap: 5px; padding: 7px 10px; font-size: 0.68rem; font-weight: 600; color: #f59e0b; opacity: 0.7; }
.action-blocked i { font-size: 0.6rem; }
/* Project history actions */
.ph-actions { display: flex; gap: 4px; margin-top: 4px; }
.ph-action-btn { padding: 3px 8px; border-radius: 4px; border: 1px solid var(--border-color); background: none; color: var(--text-color); opacity: 0.5; font-size: 0.6rem; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 4px; transition: all 0.15s; }
.ph-action-btn:hover { opacity: 0.8; }
.ph-view-report { border-color: rgba(59,130,246,0.2); }
.ph-view-report:hover, .ph-view-report.active { color: #93c5fd; border-color: rgba(59,130,246,0.4); background: rgba(59,130,246,0.06); opacity: 1; }
.ph-write-report { border-color: rgba(245,158,11,0.2); }
.ph-write-report:hover { color: #f59e0b; border-color: rgba(245,158,11,0.4); background: rgba(245,158,11,0.06); opacity: 1; }
.ph-action-btn i { font-size: 0.55rem; }

/* Inline report viewer */
.ph-report-inline { margin-top: 6px; padding: 8px; background: var(--background-color); border: 1px solid var(--border-color); border-radius: 6px; }
.ph-report-loading { font-size: 0.65rem; color: var(--text-color); opacity: 0.3; padding: 6px 0; }
.ph-report-empty { font-size: 0.65rem; color: var(--text-color); opacity: 0.2; padding: 4px 0; }

.ph-report-meta { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; align-items: center; }
.ph-report-status { font-size: 0.58rem; font-weight: 700; padding: 2px 7px; border-radius: 4px; text-transform: uppercase; }
.ph-report-status.completed { color: #22c55e; background: rgba(34,197,94,0.08); }
.ph-report-status.draft { color: #f59e0b; background: rgba(245,158,11,0.08); }
.ph-report-status.in_progress { color: #3b82f6; background: rgba(59,130,246,0.08); }
.ph-report-workflow { font-size: 0.58rem; color: var(--text-color); opacity: 0.4; font-weight: 600; }
.ph-report-date { font-size: 0.55rem; color: var(--text-color); opacity: 0.25; }

.ph-report-section { margin-bottom: 8px; }
.ph-report-section h5 { font-size: 0.65rem; font-weight: 700; color: var(--text-color); opacity: 0.5; margin: 0 0 4px; display: flex; align-items: center; gap: 5px; }
.ph-report-section h5 i { font-size: 0.55rem; }

.ph-report-field { display: flex; gap: 8px; padding: 3px 0; border-bottom: 1px solid var(--border-color); }
.ph-field-label { font-size: 0.62rem; font-weight: 700; color: var(--text-color); opacity: 0.4; min-width: 100px; flex-shrink: 0; }
.ph-field-value { font-size: 0.62rem; color: var(--text-color); opacity: 0.7; word-break: break-word; white-space: pre-wrap; }


/* Shared project board distinction */
.col-shared-badge { font-size: 0.55rem; color: #d42f3d; background: rgba(212,47,61,0.08); border: 1px solid rgba(212,47,61,0.15); padding: 1px 5px; border-radius: 3px; }
.col-shared-badge i { font-size: 0.5rem; }

/* Comment author */
.comment-author { font-size: 0.6rem; font-weight: 700; color: #93c5fd; margin-bottom: 2px; }
</style>